// src/features/practitioner/import/PractitonerImport.jsx
import FalconComponentCard from "components/common/FalconComponentCard";
import React, { useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { motion } from "framer-motion";
import HorizontalStepper from "./HorizontalStepper";
import { AnimatePresence } from "framer-motion";
import UploadStep from "./UploadStep";
import CheckingStep from "./CheckingStep";
import ResultStep from "./ResultStep";
import { CheckCircle, CloudUpload, ShieldCheck } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  selectTableData,
  selectIsExcelValid,
  selectActiveStep,
  setTableData,
  setActiveStep,
  resetImportState,
  setDuplicateTableData,
  setDuplicateRows,
} from "../config/practitionerSlice";
import {
  selectExcelData,
  selectValidationErrors,
} from "../config/practitionerSlice";
import moment from "moment";
import { genderData, maritalStatusData, nationalityData } from "../utils";
import { GetAllPractitionerList } from "../config/practitionerSliceAPi";
import { toast } from "react-toastify";
import WizardModal from "components/wizard/WizardModal";
import UploadPracitionerModal from "features/practitioner/components/UploadPracitionerModal";
import BackButton from "components/common/custom/back-button";
import paths from "routes/paths";

const PractitonerImport = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {
      excelFile: null,
    },
  });

  const dispatch = useDispatch();
  const { excelData, validationErrors, tableData, isExcelValid, activeStep } =
    useSelector((state) => state.practitioner);
  const { allPermissionType } = useSelector((state) => state.role);
  const practitonerSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Practitioner"
  );

  const [showFileError, setShowFileError] = React.useState(false);
  const [payload, setPayload] = React.useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = React.useState(false);
  const [showUploadPractitioner, setShowUploadPractitioner] =
    React.useState(false);

  // Initialize tableData when excelData or validationErrors change
  useEffect(() => {
    const data = excelData.map((row, index) => {
      const rowNum = index + 2;
      const rowErrors = validationErrors.filter((err) => err.row === rowNum);

      return {
        ...row,
        rowNum,
        isValid: rowErrors.length === 0,
        errors: rowErrors.map((e) => e.message),
      };
    });
    dispatch(setTableData(data));
  }, [excelData, validationErrors, dispatch]);

  const steps = [
    {
      label: "Upload",
      icon: CloudUpload,
      component: (
        <UploadStep
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
        />
      ),
    },
    {
      label: "Checking",
      icon: ShieldCheck,
      component: <CheckingStep />,
    },
    {
      label: "Result",
      icon: CheckCircle,
      component: <ResultStep />,
    },
  ];

  const validateFile = async () => {
    const isValid = await trigger("excelFile");
    setShowFileError(!isValid);
    return isValid;
  };

  const handleContinue = async () => {
    if (activeStep === 0) {
      const isValid = await validateFile();
      if (isValid) {
        dispatch(setActiveStep(1));
        setShowFileError(false);
      }
    } else if (activeStep === steps.length - 1) {
      const body = {
        branch: "",
        name: "",
        license_number: "",
        specialities_code: "",
        id_number: "",
        start_point: 0,
        limit: 10000000,
      };

      const practitionerData = await dispatch(
        GetAllPractitionerList(body)
      ).unwrap();

      const existingPractitioners =
        practitionerData?.data?.practitioner_details || [];

      // Convert existing IDs to strings and filter out empty values
      const existingNationalIds = new Set(
        existingPractitioners
          .map((p) => p.practitioner_nationalid?.toString() || "")
          .filter((id) => id) // Remove empty strings
      );

      const payload = {};
      const duplicates = [];

      const updatedTable = tableData.map((row, index) => {
        const key = (index + 1).toString();
        const nationalId = (
          row["National Id"]?.toString() ||
          row["Passport No"]?.toString() ||
          ""
        ).trim();

        const isDuplicate = nationalId && existingNationalIds.has(nationalId);
        if (isDuplicate) {
          const duplicateRow = {
            ...row,
            rowNum: index + 2,
            name: `${row["First Name (Eng)"] || ""} ${row["Last Name (Eng)"] || ""
              }`.trim(),
            nationalId,
            passportNo: row["Passport No"] || "",
            status: "duplicate",
          };
          duplicates.push(duplicateRow);
          return duplicateRow;
        }

        // Add to payload if not duplicate
        payload[key] = {
          practitioner_license: row["License No"]?.toString() || "",
          practitioner_license_exp: row["License Expiry"]
            ? moment(row["License Expiry"]).format("YYYY-MM-DD")
            : "",
          practitioner_title: "",
          practitioner_firstname: row["First Name (Eng)"] || "",
          practitioner_firstname_arabic: "",
          practitioner_fathername: "",
          practitioner_fathername_arabic: "",
          practitioner_familyname: row["Last Name (Eng)"] || "",
          practitioner_familyname_arabic: "",
          practitioner_specialities:
            row["speciality"]
              ?.map((item) => item?.title || item?.label)
              ?.join(",") || "",
          branch_cchi: row["Branch"] || "",
          practitioner_mobilenumber: row["Mobile No"] || "",
          practitioner_emailid: row["Email"] || "",
          practitioner_gender:
            genderData?.find((item) => item?.value === row["Gender"])?.title ||
            "",
          practitioner_dob: row["DOB"]
            ? moment(row["DOB"]).format("YYYY-MM-DD")
            : "",
          practitioner_maritalstatus:
            maritalStatusData?.find(
              (item) => item?.value === row["Marital Status"]
            )?.title || "",
          practitioner_nationality:
            nationalityData?.find((item) => item?.value === row["Nationality"])
              ?.title || "",
          practitioner_nationalid: nationalId,
          practitioner_address: "",
          practitioner_address_arabic: "",
        };

        return row; // unchanged
      });

      // ✅ update table data with new status
      dispatch(setTableData(updatedTable));

      if (duplicates.length > 0) {
        dispatch(setDuplicateRows(duplicates));

        setShowDuplicateModal(true);

        return;
      } else {
        dispatch(setDuplicateRows([]));
        setPayload({ pract_data: payload });
        setShowUploadPractitioner(true);
      }

      // Continue with API call if needed
    } else {
      dispatch(setActiveStep(activeStep + 1));
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      dispatch(setActiveStep(activeStep - 1));
    }
  };

  // Reset state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetImportState());
    };
  }, [dispatch]);

  return (
    <>
      {Number(practitonerSetting?.add) === 1 ? (
        <Form noValidate className="p-0">
          <FalconComponentCard
            className="shadow-md"
            style={{ position: "relative" }}
          >
            <FalconComponentCard.Header
              className="d-flex fixed-header justify-content-between align-items-center"
              sx={{
                position: "sticky !important",
                top: 68,
                zIndex: 1020,
                backgroundColor: "white",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <BackButton
                path={paths.practitionerList}
                title={"Practitioners Import"}
              />
              <Col xs="auto" className="d-flex gap-2">
                {activeStep > 0 && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="success"
                  onClick={handleContinue}
                  disabled={
                    (activeStep === 0 && !isExcelValid) ||
                    (activeStep === 1 && tableData.some((row) => !row.isValid))
                  }
                >
                  {activeStep === steps.length - 1 ? "Finish" : "Continue"}
                </Button>
              </Col>
            </FalconComponentCard.Header>
            <FalconComponentCard.Body>
              <Row>
                <Col xs={12}>
                  <HorizontalStepper
                    isHorizaontal
                    steps={steps}
                    activeStep={activeStep}
                  />
                </Col>
                <Col xs={12}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeStep}
                      initial={{ opacity: 0, x: -100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                    >
                      {steps[activeStep]?.component || null}
                    </motion.div>
                  </AnimatePresence>
                </Col>
              </Row>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Form>
      ) : (
        <div className="py-6 text-center border rounded-3mb-3">
          <h5 className="text-danger">Permission Denied</h5>
          <p className="text-muted fs-11">
            You do not have permission to import Practitioner.
          </p>
        </div>
      )}
      <WizardModal
        modal={showDuplicateModal}
        setModal={() => setShowDuplicateModal(false)}
        title="Duplicate Practitioner Record(s)"
        description="Each practitioner must have a unique National ID or Passport No. please edit national id or passport no or delete the record"
      />
      <UploadPracitionerModal
        payload={payload}
        onClose={() => setShowUploadPractitioner(false)}
        show={showUploadPractitioner}
      />
    </>
  );
};

export default PractitonerImport;
